import * as React from 'react';
import cx from 'classnames';
import format from 'date-fns/format';
import { useHistory } from 'react-router-dom';
import RouterLink from './RouterLink';
import { addClassName } from '../utils/classes';
import { getBaseUrl } from '../utils/globals';
import ArticleBody from '../articleBody';
import useArticle from '../hooks/useArticle';
import useIsArticleActive from '../hooks/useIsArticleActive';
import useArticleFragment from '../hooks/useArticleFragment';
/**
 * Render the title of an article, if you don't pass in a articleId
 * then it will use the current article.
 */
export const Title = ({ articleId }) => {
    const article = useArticleFragment(articleId);
    return React.createElement(React.Fragment, null, article && article.title);
};
Title.displayName = 'Article.Title';
/**
 * Render the summary of an article, if you don't pass in a articleId
 * then it will use the current article.
 * This will fetch the article if we don't have it already.
 */
export const Summary = ({ articleId, className }) => {
    const article = useArticleFragment(articleId);
    if (!article || !article.summary)
        return null;
    return (React.createElement("div", { className: cx(className, addClassName('article-summary')) }, article.summary));
};
Summary.displayName = 'Article.Summary';
/**
 * Render the body of an article, if you don't pass in a articleId
 * then it will use the current article.
 * This will fetch the article if we don't have it already or we don't have it's body.
 */
export const Body = ({ className, noStyles, onClick }) => {
    const article = useArticle();
    const history = useHistory();
    const baseUrl = getBaseUrl();
    if (!article || !article.body)
        return null;
    const onClickInterlink = (articleId) => {
        // NOTE: the slug is missing, but probably not a big deal for interlinks.
        const url = `${baseUrl}/articles/${articleId}`;
        history.push(url);
    };
    return (React.createElement(ArticleBody, { articleId: article.id, className: className, html: article.body, noStyles: noStyles, onClickInterlink: onClickInterlink, onClick: onClick }));
};
Body.displayName = 'Article.Body';
/**
 * Render the last updated time from the current article.
 */
// TODO: this needs localizing
export const LastUpdatedAt = () => {
    const article = useArticle();
    if (!article || !article.lastUpdatedAt)
        return null;
    return (React.createElement(React.Fragment, null,
        "Last Updated:",
        ' ',
        format(new Date(article.lastUpdatedAt), 'MMM d, yyyy h:mma')));
};
LastUpdatedAt.displayName = 'Article.LastUpdatedAt';
/**
 * This will render an A tag with a href that points to an article page.
 * If you pass in a articleId then it will use that otherwise it will
 * use the current article context.
 */
export const Link = ({ children, articleId, slug, className, 'data-testid': dataTestId, ...props }) => {
    const baseUrl = getBaseUrl();
    const fragment = useArticleFragment(articleId);
    const _slug = slug ? slug : fragment ? fragment.slug : articleId;
    if (!_slug)
        return null;
    return (React.createElement(RouterLink, Object.assign({ className: cx(className, addClassName('article-link')), to: `${baseUrl}/articles/${_slug}`, "data-testid": dataTestId }, props), children));
};
Link.displayName = 'Article.Link';
/**
 * Renders out the child content if the article either from the articleId prop or if not provided the
 * current article is active
 * If a render prop is passed as the child then a boolean will be provided with the is active state
 */
export const IsActive = ({ children, articleId }) => {
    const isActive = useIsArticleActive(articleId);
    if (typeof children === 'function') {
        return React.createElement(React.Fragment, null, children(isActive));
    }
    if (!isActive)
        return null;
    return React.createElement(React.Fragment, null, children);
};
IsActive.displayName = 'Article.IsActive';
/**
 * Renders out the child content if the article either from the articleId prop or if not provided the
 * current article is NOT active
 * If a render prop is passed as the child then a boolean will be provided with the is non-active state
 */
export const IsNotActive = ({ children, articleId }) => {
    const isActive = useIsArticleActive(articleId);
    if (typeof children === 'function') {
        return React.createElement(React.Fragment, null, children(!isActive));
    }
    if (isActive)
        return null;
    return React.createElement(React.Fragment, null, children);
};
IsNotActive.displayName = 'Article.IsNotActive';
export const Article = ({ children }) => {
    const article = useArticle();
    if (!article)
        return null;
    return React.createElement(React.Fragment, null, children(article));
};
Article.displayName = 'Article.Article';
// export function MetaDescription = () {
// }
//# sourceMappingURL=article.js.map